import { nextKeptTime, outputLength, sourceToOutputTime, type PlayRange } from '../time/ranges';
import type { SequenceLayout } from './sequence';

/**
 * What happens where two clips meet: a straight cut, or the picture dipping
 * through black (or white) and coming back. The dip is laid over the seam,
 * half before and half after it, so the video keeps its length and the sound
 * runs on as it is.
 */
export type TransitionStyle = 'none' | 'black' | 'white';
export const TRANSITION_STYLES: readonly TransitionStyle[] = ['none', 'black', 'white'];

/** Lengths offered, in seconds, from a quick blink to a slow scene change. */
export const TRANSITION_LENGTHS = [0.3, 0.6, 1] as const;

export interface TransitionSettings {
  readonly style: TransitionStyle;
  /** Seconds the whole dip lasts. */
  readonly duration: number;
}

export const DEFAULT_TRANSITION: TransitionSettings = { style: 'none', duration: 0.6 };

/** What the frame is dipped into at a moment: the colour and how far (0..1). */
export interface Dip {
  readonly color: 'black' | 'white';
  readonly amount: number;
}

/**
 * Where clips meet in the finished video (output seconds, ascending). A seam
 * whose surroundings were cut away lands where the edit picks up again; seams
 * at the very start or end, where there is nothing to dip from or into, are left out.
 */
export function outputSeams(row: Pick<SequenceLayout, 'clips'>, keep: readonly PlayRange[]): number[] {
  const seams = new Set<number>();
  const total = outputLength(keep);
  for (const clip of row.clips.slice(1)) {
    const kept = nextKeptTime(keep, clip.start);
    const at = kept === null ? null : sourceToOutputTime(keep, kept);
    if (at === null || at <= 1e-3 || at >= total - 1e-3) continue;
    seams.add(Math.round(at * 1000) / 1000);
  }
  return [...seams].sort((a, b) => a - b);
}

const smooth = (x: number): number => x * x * (3 - 2 * x);

/**
 * How dipped the picture is at output time `t`: fully at a seam, easing to
 * nothing half the transition away on either side. Null when it isn't.
 */
export function dipAt(seams: readonly number[], settings: TransitionSettings, t: number): Dip | null {
  if (settings.style === 'none' || seams.length === 0 || !(settings.duration > 0)) return null;
  const half = settings.duration / 2;
  let nearest = Infinity;
  for (const seam of seams) {
    const distance = Math.abs(t - seam);
    if (distance < nearest) nearest = distance;
    if (seam > t + half) break;
  }
  if (nearest >= half) return null;
  const amount = smooth(1 - nearest / half);
  return amount > 0.001 ? { color: settings.style, amount } : null;
}

const pick = <T>(value: unknown, options: readonly T[], fallback: T): T => (options.includes(value as T) ? (value as T) : fallback);

/** Transition settings read back from storage, anything unknown replaced by the default. */
export function parseTransition(value: unknown): TransitionSettings {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<Record<keyof TransitionSettings, unknown>>;
  const duration = typeof raw.duration === 'number' && raw.duration > 0 && raw.duration <= 2 ? raw.duration : DEFAULT_TRANSITION.duration;
  return { style: pick(raw.style, TRANSITION_STYLES, DEFAULT_TRANSITION.style), duration };
}
